"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { SUBTITLE_CONVERTER_STRINGS } from "@/components/tools/subtitle-converter";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { ConvertResult } from "@/lib/tool-kit/qualitative-text/subtitle-convert";
import {
  formatSubtitleTime,
  type SubtitleFormat,
} from "@/lib/tool-kit/qualitative-text/subtitles";

type Strings = typeof SUBTITLE_CONVERTER_STRINGS;

const PREVIEW_ROWS = 5;
const PREVIEW_CHARS = 60;
const RE_SPACES = /\s+/g;

function oneLine(text: string): string {
  const flat = text.replace(RE_SPACES, " ");
  return flat.length > PREVIEW_CHARS
    ? `${flat.slice(0, PREVIEW_CHARS)}…`
    : flat;
}

function CheckList({
  result,
  strings,
}: {
  result: ConvertResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const lines = [
    { count: result.checks.outOfOrder, message: strings.checkOutOfOrder },
    { count: result.checks.overlapping, message: strings.checkOverlap },
    { count: result.checks.empty, message: strings.checkEmpty },
    { count: result.checks.zeroLength, message: strings.checkZero },
  ].filter((line) => line.count > 0);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.checksHeading}</h3>
      {lines.length === 0 ? (
        <p className={HINT}>{strings.checksClean}</p>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
          {lines.map((line) => (
            <li key={line.message}>
              {tPlural(line.message, locale, line.count, {
                count: fmt(line.count),
              })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SubtitleResults({
  result,
  strings,
  target,
}: {
  result: ConvertResult;
  strings: Strings;
  target: SubtitleFormat;
}) {
  const fmt = useToolNumber();
  const lastEnd = Math.max(0, ...result.after.map((cue) => cue.end));
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {result.problem === "noCues" ? <Notice>{strings.noCues}</Notice> : null}
      {result.problem === "badValue" ? (
        <Notice>{strings.badValue}</Notice>
      ) : null}
      {result.problem === null ? (
        <>
          <p className={HINT}>
            {t(strings.detected, {
              format: strings.targets[result.format ?? "srt"],
            })}
          </p>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.statCues}
              value={fmt(result.after.length)}
            />
            <StatCard
              label={strings.statSpan}
              value={t(strings.spanFormat, {
                start: formatSubtitleTime(result.after[0]?.start ?? 0, "."),
                end: formatSubtitleTime(lastEnd, "."),
              })}
            />
            {result.dropped > 0 ? (
              <StatCard
                label={strings.statDropped}
                value={fmt(result.dropped)}
              />
            ) : null}
            {result.clamped > 0 ? (
              <StatCard
                label={strings.statClamped}
                value={fmt(result.clamped)}
              />
            ) : null}
          </StatGrid>
          <CheckList result={result} strings={strings} />
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.previewHeading}</h3>
            <DataTable
              head={[
                strings.colNumber,
                strings.colBefore,
                strings.colAfter,
                strings.colText,
              ]}
              rows={result.after
                .slice(0, PREVIEW_ROWS)
                .map((cue, index) => [
                  fmt(index + 1),
                  formatSubtitleTime(result.originalStarts[index] ?? 0, "."),
                  formatSubtitleTime(cue.start, "."),
                  oneLine(cue.text),
                ])}
            />
          </div>
          <div className="space-y-2">
            <label className={LABEL} htmlFor="sc-output">
              {strings.outputLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="sc-output"
              readOnly
              rows={10}
              value={result.output}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={result.output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    strings.filenames[target],
                    result.output,
                    target === "vtt"
                      ? "text/vtt;charset=utf-8"
                      : "text/plain;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
